/* eslint-disable max-lines */
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  myOrders: {},
  isOrdersLoading: false,
  ordersLoadError: false,
  paymentStatus: {
    isLoading: false,
    data: {},
  },
  senderData: {
    mobileCountryCode: "",
    senderCountry: "",
  },
  notificationSettingsList: {
    isLoaded: false,
    isError: false,
    isSaved: false,
    errorMessage: "",
    data: {},
  },
  chatBotConfigData: {},
  isChatBotConfigLoaded: false,
  callChatTime: {},
  isCallChatTimeLoaded: false,
  partnersList: {
    isLoaded: false,
    isError: false,
    errorMessage: "",
    data: [],
  },
  partnerLoyaltyContentIdDetails: {},
  isPartnerLoyaltyContentIdDetailsLoaded: false,
  walletStatement: {
    isLoading: false,
    statements: [],
    page: 1,
    totalCount: 0,
    errorMessage: "",
  },
  giftVouchers: {
    isLoading: false,
    isError: false,
    unused: [],
    used: [],
    currencies: [],
  },
  profile: {
    customerData: {},
    isLoading: false,
    isUpdating: false,
    isSuccess: false,
    showError: false,
    mobileOtp: {
      isLoading: false,
      isOtpSent: false,
      errorMessage: "",
      verifySuccess: false,
    },
  },
  orderDetailsDrawer: {
    isOpen: false,
    subOrderId: null,
    mode: "view-details",
  },
  isOrderDeleted: false,
};

const myAccountSlice = createSlice({
  name: "myAccount",
  initialState,
  reducers: {
    requestToFetchMyOrders: (state, action) => {
      const silent = Boolean(action.payload?.silent);
      const hasOrders = state.myOrders && Object.keys(state.myOrders).length > 0;
      if (!silent || !hasOrders) {
        state.isOrdersLoading = true;
      }
      state.ordersLoadError = false;
    },
    responseFromFetchMyOrders: (state, action) => {
      state.isOrdersLoading = false;
      state.myOrders = action.payload ?? {};
    },
    failedToFetchMyOrders: (state) => {
      state.isOrdersLoading = false;
      state.ordersLoadError = true;
    },
    requestToFetchPartnerLoyaltyDetails: () => {},
    responseFromFetchPartnerLoyaltyDetails: (state, action) => {
      state.partnerLoyaltyContentIdDetails = action.payload ?? {};
      state.isPartnerLoyaltyContentIdDetailsLoaded = true;
    },
    failedToFetchPartnerLoyaltyDetails: (state) => {
      state.isPartnerLoyaltyContentIdDetailsLoaded = true;
    },
    requestToFetchChatBotConfig: () => {},
    responseFromFetchChatBotConfig: (state, action) => {
      state.chatBotConfigData = action.payload ?? {};
      state.isChatBotConfigLoaded = true;
    },
    requestToFetchCallChatTime: () => {},
    responseFromFetchCallChatTime: (state, action) => {
      state.callChatTime = action.payload ?? {};
      state.isCallChatTimeLoaded = true;
    },
    failedToFetchCallChatTime: (state) => {
      state.isCallChatTimeLoaded = true;
    },
    setSenderCountryData: (state, action) => {
      state.senderData = action.payload ?? state.senderData;
    },
    requestToCheckPaymentStatus: (state) => {
      state.paymentStatus.isLoading = true;
    },
    responseFromCheckPaymentStatus: (state, action) => {
      state.paymentStatus.isLoading = false;
      state.paymentStatus.data = action.payload ?? {};
    },
    failedToCheckPaymentStatus: (state) => {
      state.paymentStatus.isLoading = false;
    },
    responseFromFetchNotificationSettings: (state, action) => {
      state.notificationSettingsList = {
        isLoaded: true,
        isError: false,
        isSaved: false,
        errorMessage: "",
        data: action.payload ?? {},
      };
    },
    failedToFetchNotificationSettings: (state, action) => {
      state.notificationSettingsList = {
        isLoaded: true,
        isError: true,
        isSaved: false,
        errorMessage: action.payload ?? "",
        data: {},
      };
    },
    responseFromFetchPartnersList: (state, action) => {
      state.partnersList = {
        isLoaded: true,
        isError: false,
        errorMessage: "",
        data: action.payload ?? [],
      };
    },
    failedToFetchPartnersList: (state) => {
      state.partnersList = {
        isLoaded: true,
        isError: true,
        errorMessage: "",
        data: [],
      };
    },
    requestToFetchNotificationSettings: (state) => {
      state.notificationSettingsList.isLoaded = false;
      state.notificationSettingsList.isError = false;
      state.notificationSettingsList.isSaved = false;
    },
    requestToSaveNotificationSettings: (state) => {
      state.notificationSettingsList.isError = false;
      state.notificationSettingsList.isSaved = false;
    },
    responseFromSaveNotificationSettings: (state, action) => {
      state.notificationSettingsList = {
        isLoaded: true,
        isError: false,
        isSaved: true,
        errorMessage: "",
        data: action.payload ?? state.notificationSettingsList.data,
      };
    },
    failedToSaveNotificationSettings: (state) => {
      state.notificationSettingsList.isError = true;
    },
    requestToFetchPartnersList: () => {},
    requestToFetchSenderCountry: () => {},
    requestToFetchWalletStatement: (state) => {
      state.walletStatement.isLoading = true;
    },
    responseFromFetchWalletStatement: (state, action) => {
      const { statements, page, totalCount, append } = action.payload ?? {};
      state.walletStatement.isLoading = false;
      state.walletStatement.page = page ?? 1;
      state.walletStatement.totalCount = totalCount ?? 0;
      state.walletStatement.statements = append
        ? [...state.walletStatement.statements, ...(statements ?? [])]
        : (statements ?? []);
      state.walletStatement.errorMessage = "";
    },
    failedToFetchWalletStatement: (state, action) => {
      state.walletStatement.isLoading = false;
      state.walletStatement.errorMessage = action.payload ?? "";
    },
    requestToFetchCustomerProfile: (state) => {
      state.profile.isLoading = true;
      state.profile.showError = false;
    },
    responseFromFetchCustomerProfile: (state, action) => {
      state.profile.isLoading = false;
      const customer = action.payload?.customer ?? action.payload ?? {};
      state.profile.customerData = {
        ...customer,
        isMobileValidated: Boolean(customer?.isMobileValidated),
        isEmailValidated: Boolean(customer?.isEmailValidated),
      };
    },
    failedToFetchCustomerProfile: (state) => {
      state.profile.isLoading = false;
      state.profile.showError = true;
    },
    requestToUpdateCustomerProfile: (state) => {
      state.profile.isUpdating = true;
      state.profile.isSuccess = false;
      state.profile.showError = false;
    },
    responseFromUpdateCustomerProfile: (state, action) => {
      state.profile.isUpdating = false;
      state.profile.isSuccess = true;
      if (action.payload) {
        state.profile.customerData = { ...state.profile.customerData, ...action.payload };
      }
    },
    failedToUpdateCustomerProfile: (state) => {
      state.profile.isUpdating = false;
      state.profile.showError = true;
    },
    resetProfileUpdateSuccess: (state) => {
      state.profile.isSuccess = false;
    },
    requestProfileSendMobileOtp: (state) => {
      state.profile.mobileOtp.isLoading = true;
      state.profile.mobileOtp.errorMessage = "";
    },
    profileMobileOtpSent: (state) => {
      state.profile.mobileOtp.isLoading = false;
      state.profile.mobileOtp.isOtpSent = true;
    },
    requestProfileVerifyMobileOtp: (state) => {
      state.profile.mobileOtp.isLoading = true;
      state.profile.mobileOtp.errorMessage = "";
    },
    profileMobileOtpVerified: (state) => {
      state.profile.mobileOtp = {
        isLoading: false,
        isOtpSent: false,
        errorMessage: "",
        verifySuccess: true,
      };
    },
    profileMobileOtpError: (state, action) => {
      state.profile.mobileOtp.isLoading = false;
      state.profile.mobileOtp.errorMessage = action.payload || "";
    },
    resetProfileMobileOtp: (state) => {
      state.profile.mobileOtp = {
        isLoading: false,
        isOtpSent: false,
        errorMessage: "",
        verifySuccess: false,
      };
    },
    setProfileMobileValidated: (state, action) => {
      state.profile.customerData = {
        ...state.profile.customerData,
        isMobileValidated: true,
        phoneNum: action.payload?.phoneNum,
        countryCode: action.payload?.countryCode,
      };
    },
    requestToFetchGiftVouchers: (state) => {
      state.giftVouchers.isLoading = true;
      state.giftVouchers.isError = false;
    },
    responseFromFetchGiftVouchers: (state, action) => {
      state.giftVouchers.isLoading = false;
      state.giftVouchers.unused = action.payload?.unused ?? [];
      state.giftVouchers.used = action.payload?.used ?? [];
      state.giftVouchers.currencies = action.payload?.currencies ?? [];
    },
    failedToFetchGiftVouchers: (state) => {
      state.giftVouchers.isLoading = false;
      state.giftVouchers.isError = true;
    },
    openAccountOrderDetailsDrawer: (state, action) => {
      const { subOrderId, mode = "view-details" } = action.payload ?? {};
      state.orderDetailsDrawer = {
        isOpen: true,
        subOrderId: subOrderId ?? null,
        mode,
      };
    },
    closeAccountOrderDetailsDrawer: (state) => {
      state.orderDetailsDrawer = {
        isOpen: false,
        subOrderId: null,
        mode: "view-details",
      };
    },
    requestToDeleteExpiredOrder: () => {},
    requestToDeleteDeliveredOrder: () => {},
    responseFromOrderDelete: (state) => {
      state.isOrderDeleted = true;
    },
    resetOrderDeletedFlag: (state) => {
      state.isOrderDeleted = false;
    },
  },
});

export const {
  requestToFetchMyOrders,
  responseFromFetchMyOrders,
  failedToFetchMyOrders,
  requestToFetchPartnerLoyaltyDetails,
  responseFromFetchPartnerLoyaltyDetails,
  failedToFetchPartnerLoyaltyDetails,
  requestToFetchChatBotConfig,
  responseFromFetchChatBotConfig,
  requestToFetchCallChatTime,
  responseFromFetchCallChatTime,
  failedToFetchCallChatTime,
  setSenderCountryData,
  requestToCheckPaymentStatus,
  responseFromCheckPaymentStatus,
  failedToCheckPaymentStatus,
  responseFromFetchNotificationSettings,
  failedToFetchNotificationSettings,
  responseFromFetchPartnersList,
  failedToFetchPartnersList,
  requestToFetchNotificationSettings,
  requestToSaveNotificationSettings,
  responseFromSaveNotificationSettings,
  failedToSaveNotificationSettings,
  requestToFetchPartnersList,
  requestToFetchSenderCountry,
  requestToFetchWalletStatement,
  responseFromFetchWalletStatement,
  failedToFetchWalletStatement,
  requestToFetchGiftVouchers,
  responseFromFetchGiftVouchers,
  failedToFetchGiftVouchers,
  requestToFetchCustomerProfile,
  responseFromFetchCustomerProfile,
  failedToFetchCustomerProfile,
  requestToUpdateCustomerProfile,
  responseFromUpdateCustomerProfile,
  failedToUpdateCustomerProfile,
  resetProfileUpdateSuccess,
  requestProfileSendMobileOtp,
  profileMobileOtpSent,
  requestProfileVerifyMobileOtp,
  profileMobileOtpVerified,
  profileMobileOtpError,
  resetProfileMobileOtp,
  setProfileMobileValidated,
  openAccountOrderDetailsDrawer,
  closeAccountOrderDetailsDrawer,
  requestToDeleteExpiredOrder,
  requestToDeleteDeliveredOrder,
  responseFromOrderDelete,
  resetOrderDeletedFlag,
} = myAccountSlice.actions;

export default myAccountSlice.reducer;
